import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  BiaConfig,
  BiaConfirmUploadsRs,
  BiaCreateItemRq,
  BiaCreateProductsRs,
  BiaImage,
  BiaImagePage,
  BiaPayloadPage,
  BiaPlan,
  BiaPlanBody,
  BiaPlanExport,
  BiaPlanExportFormat,
  BiaPlanVersionInfo,
  BiaSession,
  BiaSessionFilter,
  BiaSessionListItem,
  BiaUploadFileRq,
  BiaUploadUrlsRs,
  CreateBiaSessionForm,
  UpdateBiaImageForm,
  UpdateBiaSessionForm,
} from "../../interfaces/BulkImageAnalysisV2Object";
import { deleteRequest, getRequest, patchRequest, postRequest, putRequest } from "../http/httpRequest";
import { ApiEnvelopeError, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import {
  BIA_CLIENT_ERROR,
  BIA_ERROR,
  BIA_GENERIC_ERROR,
  biaErrorMessage,
  biaRetryDelay,
  isBiaCode,
  isRetryableBiaCode,
} from "../helpers/BulkImageAnalysisV2Errors";
import {
  buildConfirmPlanPayload,
  buildConfirmUploadsPayload,
  buildCreateProductsPayload,
  buildCreateSessionPayload,
  buildRestorePayload,
  buildRetryGeneratePayload,
  buildRevisePayload,
  buildUnconfirmPayload,
  buildUpdateImagePayload,
  buildUpdatePlanPayload,
  buildUpdateSessionPayload,
  buildUploadUrlsPayload,
} from "../converters/uiToBackend/BulkImageAnalysisV2Convertor";
import {
  convertBiaConfig,
  convertBiaConfirmUploads,
  convertBiaCreateProducts,
  convertBiaImageOrNull,
  convertBiaImagePage,
  convertBiaPayloadPage,
  convertBiaPlan,
  convertBiaPlanExport,
  convertBiaPlanVersions,
  convertBiaSession,
  convertBiaSessionList,
  convertBiaUploadUrls,
  looksLikeSession,
} from "../converters/backendToUi/bulkImageAnalysisV2Convertor";

/**
 * Client for Smart photos (bulk image analysis v2) — `/api/v2/bulk-image-analysis`.
 * Contract: interfaces/BulkImageAnalysisV2Object.ts (mirrors json-lib-api's
 * bulkimageanalysis models) and product/docs/bulk-image-analysis-v2-design.md §9.
 *
 * Like catalogue import, failures come back with their REAL HTTP status
 * (400/401/403/404/409/413/415/422/429/502/503) and the NineBusinessRs envelope
 * as the body, so unwrapEnvelope parses the error and we rethrow it as a
 * BiaApiError carrying the code (16301-16399), the HTTP status and
 * `additionalDetails` ({ retryable, sessionId?, currentVersion?, problems? }).
 *
 * Retry rule: network failures and 5xx are retried with backoff for GETs ONLY.
 * POST / PUT / PATCH / DELETE are never repeated automatically here — the create
 * endpoints are idempotent per item, but replaying a revise or a plan edit is the
 * curator's decision. (Uploads have their own per-file retry in the upload queue.)
 *
 * ASSUMPTION (isolated here, easy to change): the design lists the action
 * endpoints (plan, revise, confirm, generate, cancel, ...) without a response
 * body. Every action therefore returns the session: the response is used when it
 * looks like a session, otherwise GET /sessions/{id} is issued (see
 * `settleSession`). Anything that changes the plan is picked up by the UI through
 * `session.currentPlanVersion`, so no action has to return a plan.
 */
const BEARER = true;

/** A failed Smart photos call: the code, the HTTP status and whether the same call may work later. */
export class BiaApiError extends ApiEnvelopeError {
  httpStatus: number;
  retryable: boolean;

  constructor(
    errorCode: string,
    message: string,
    httpStatus: number,
    additionalDetails?: Record<string, unknown>
  ) {
    super(errorCode, message, additionalDetails);
    this.name = "BiaApiError";
    this.httpStatus = httpStatus;
    const flagged = additionalDetails?.retryable;
    this.retryable =
      typeof flagged === "boolean"
        ? flagged
        : isRetryableBiaCode(errorCode) || httpStatus >= 502 || httpStatus === 0;
  }

}

/** Network failures, gateway errors and 5xx — the only failures a GET is retried for. */
function isTransient(error: unknown): boolean {
  return (
    error instanceof BiaApiError &&
    (error.errorCode === BIA_CLIENT_ERROR.NETWORK || error.httpStatus >= 500)
  );
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const MAX_GET_RETRIES = 2;

interface SendOptions {
  /** GETs only. */
  retry?: boolean;
}

/** One request -> converted payload, with the error mapping and (for GETs) retry described above. */
async function send<T>(
  make: () => Promise<Response>,
  convert: (contents: unknown) => T,
  { retry = false }: SendOptions = {}
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      let response: Response;
      try {
        response = await make();
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") throw error;
        throw new BiaApiError(
          BIA_CLIENT_ERROR.NETWORK,
          biaErrorMessage(BIA_CLIENT_ERROR.NETWORK, BIA_GENERIC_ERROR),
          0
        );
      }
      const status = response.status;
      // A bodiless success (204) is still a success.
      if (status === 204) return convert(null);
      try {
        return convert(await unwrapEnvelope<unknown>(response));
      } catch (error) {
        if (error instanceof ApiEnvelopeError && !(error instanceof BiaApiError)) {
          throw new BiaApiError(error.errorCode, error.message, status, error.additionalDetails);
        }
        throw error;
      }
    } catch (error) {
      if (retry && attempt < MAX_GET_RETRIES && isTransient(error)) {
        await sleep(biaRetryDelay(attempt));
        continue;
      }
      throw error;
    }
  }
}

/**
 * Converts any error thrown by this service into curator-facing text. Request
 * errors use the server's message (curator-safe by contract, and it carries
 * specifics like counts); gateway noise and unknown codes get our copy.
 */
export function toFriendlyBiaError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    if (isBiaCode(error.errorCode)) {
      const server = error.message?.trim();
      return server && server !== BIA_GENERIC_ERROR ? server : biaErrorMessage(error.errorCode, BIA_GENERIC_ERROR);
    }
    if (error.errorCode === BIA_CLIENT_ERROR.NETWORK) {
      return biaErrorMessage(error.errorCode, BIA_GENERIC_ERROR);
    }
    if (/^HTTP_5\d\d$/.test(error.errorCode)) {
      return "The service is temporarily unavailable. Please try again in a moment.";
    }
    if (error.errorCode === "HTTP_401") return biaErrorMessage(BIA_ERROR.UNAUTHENTICATED, BIA_GENERIC_ERROR);
    return error.message?.trim() || BIA_GENERIC_ERROR;
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return biaErrorMessage(BIA_CLIENT_ERROR.ABORTED, "Cancelled.");
  }
  return error instanceof Error && error.message ? error.message : BIA_GENERIC_ERROR;
}

// ── Paths ────────────────────────────────────────────────────────────────────

const BASE = ServiceEndpoints.BULK_IMAGE_ANALYSIS_V2;
const enc = encodeURIComponent;

const sessionPath = (sessionId: string, suffix = ""): string => `${BASE}/sessions/${enc(sessionId)}${suffix}`;

// ── Config and sessions ──────────────────────────────────────────────────────

/** GET /config — the feature switch, limits and the caller's remaining photos today. */
function getConfig(): Promise<BiaConfig> {
  return send(() => getRequest(`${BASE}/config`, BEARER), convertBiaConfig, { retry: true });
}

/**
 * GET /sessions?businessId=&status= — `ACTIVE` is any non-terminal status. A
 * curator sees their own sessions, an admin everyone's.
 */
function listSessions(businessId: string, filter: BiaSessionFilter): Promise<BiaSessionListItem[]> {
  const params = new URLSearchParams({ businessId, status: filter });
  return send(() => getRequest(`${BASE}/sessions?${params.toString()}`, BEARER), convertBiaSessionList, {
    retry: true,
  });
}

/** GET /sessions/{id} — the session with progress and counters; the polling target. */
function getSession(sessionId: string): Promise<BiaSession> {
  return send(() => getRequest(sessionPath(sessionId), BEARER), convertBiaSession, { retry: true });
}

/** POST /sessions — creates the session (AWAITING_UPLOAD). SESSION_ALREADY_ACTIVE (409) carries `sessionId`. */
function createSession(form: CreateBiaSessionForm): Promise<BiaSession> {
  return send(() => postRequest(`${BASE}/sessions`, buildCreateSessionPayload(form), BEARER), convertBiaSession);
}

/**
 * Turns an action's response into the session: used as is when it looks like
 * one, otherwise the session is fetched. Keeps every action thunk's contract
 * "resolves with the fresh session" whatever the backend answers with.
 */
async function settleSession(sessionId: string, contents: unknown): Promise<BiaSession> {
  if (looksLikeSession(contents)) {
    const session = convertBiaSession(contents);
    if (session.sessionId === sessionId) return session;
  }
  return getSession(sessionId);
}

/** POST an action and resolve with the fresh session. */
async function postForSession(sessionId: string, suffix: string, body: unknown): Promise<BiaSession> {
  const contents = await send(() => postRequest(sessionPath(sessionId, suffix), body, BEARER), (c) => c);
  return settleSession(sessionId, contents);
}

/** PATCH /sessions/{id} — customer instructions / curator notes. */
async function updateSession(sessionId: string, form: UpdateBiaSessionForm): Promise<BiaSession> {
  const contents = await send(
    () => patchRequest(sessionPath(sessionId), buildUpdateSessionPayload(form), BEARER),
    (c) => c
  );
  return settleSession(sessionId, contents);
}

/** POST /sessions/{id}/cancel — any non-terminal status. */
function cancelSession(sessionId: string): Promise<BiaSession> {
  return postForSession(sessionId, "/cancel", {});
}

// ── Images ───────────────────────────────────────────────────────────────────

/** GET /sessions/{id}/images?cursor= — one page of photos with thumbnails, notes and flags. */
function listImages(sessionId: string, cursor?: string | null): Promise<BiaImagePage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return send(
    () => getRequest(sessionPath(sessionId, `/images${query ? `?${query}` : ""}`), BEARER),
    convertBiaImagePage,
    { retry: true }
  );
}

/** POST /sessions/{id}/images:batch — presigned PUT urls for up to `uploadBatchSize` files. */
function requestUploadUrls(sessionId: string, files: BiaUploadFileRq[]): Promise<BiaUploadUrlsRs> {
  return send(
    () => postRequest(sessionPath(sessionId, "/images:batch"), buildUploadUrlsPayload(files), BEARER),
    convertBiaUploadUrls
  );
}

/** POST /sessions/{id}/images:confirm — the server checks the objects, hashes and de-duplicates them. */
function confirmUploads(sessionId: string, imageIds: string[]): Promise<BiaConfirmUploadsRs> {
  return send(
    () => postRequest(sessionPath(sessionId, "/images:confirm"), buildConfirmUploadsPayload(imageIds), BEARER),
    convertBiaConfirmUploads
  );
}

/**
 * PATCH /sessions/{id}/images/{imageId} — exclude / include (the curator overrides the model).
 * Resolves with the fresh session (from the body when it is one, else fetched) and the image
 * when the body carried one.
 */
async function patchImage(
  sessionId: string,
  imageId: string,
  form: UpdateBiaImageForm
): Promise<{ image: BiaImage | null; session: BiaSession }> {
  const contents = await send(
    () => patchRequest(sessionPath(sessionId, `/images/${enc(imageId)}`), buildUpdateImagePayload(form), BEARER),
    (c) => c
  );
  const image = looksLikeSession(contents) ? null : convertBiaImageOrNull(contents);
  return { image, session: await settleSession(sessionId, contents) };
}

/** DELETE /sessions/{id}/images/{imageId} — allowed until the plan is confirmed. Resolves with the fresh session. */
async function deleteImage(sessionId: string, imageId: string): Promise<BiaSession> {
  const contents = await send(
    () => deleteRequest(sessionPath(sessionId, `/images/${enc(imageId)}`), BEARER),
    (c) => c
  );
  return settleSession(sessionId, contents);
}

/** Why a PUT to storage failed: connectivity / 5xx, an expired signature, a refusal, or our own abort. */
export type BiaUploadFailure = "network" | "expired" | "rejected" | "aborted";

export class BiaUploadError extends Error {
  kind: BiaUploadFailure;
  status: number;

  constructor(kind: BiaUploadFailure, status: number) {
    super(`Photo upload failed (${kind}${status ? ` ${status}` : ""})`);
    this.name = "BiaUploadError";
    this.kind = kind;
    this.status = status;
  }
}

/**
 * PUT of one photo to its presigned storage URL, with progress. XHR rather than
 * fetch because only XHR reports upload progress. Resolves on 2xx; rejects with
 * a BiaUploadError. `signal` aborts the request.
 */
function uploadToSignedUrl(
  blob: Blob,
  uploadUrl: string,
  contentType: string,
  onProgress?: (event: { loaded: number; total: number }) => void,
  signal?: AbortSignal
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new BiaUploadError("aborted", 0));
      return;
    }
    const xhr = new XMLHttpRequest();
    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const onAbort = () => {
      xhr.abort();
    };
    signal?.addEventListener("abort", onAbort);

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.({ loaded: event.loaded, total: event.total });
    };
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ loaded: blob.size, total: blob.size });
        resolve();
      } else if (xhr.status === 401 || xhr.status === 403) {
        // A signed PUT URL answers 403 once its signature has expired.
        reject(new BiaUploadError("expired", xhr.status));
      } else if (xhr.status >= 500 || xhr.status === 0 || xhr.status === 429) {
        reject(new BiaUploadError("network", xhr.status));
      } else {
        reject(new BiaUploadError("rejected", xhr.status));
      }
    };
    xhr.onerror = () => {
      cleanup();
      reject(new BiaUploadError("network", 0));
    };
    xhr.ontimeout = () => {
      cleanup();
      reject(new BiaUploadError("network", 0));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new BiaUploadError("aborted", 0));
    };
    xhr.send(blob);
  });
}

// ── Plan ─────────────────────────────────────────────────────────────────────

/** POST /sessions/{id}/plan — starts the PLAN job (notes, then tree). Also re-plans a stale plan. */
function startPlan(sessionId: string): Promise<BiaSession> {
  return postForSession(sessionId, "/plan", {});
}

/** GET /sessions/{id}/plan?version= — the full plan (the current one when no version is given). */
function getPlan(sessionId: string, version?: number): Promise<BiaPlan> {
  const query = version !== undefined ? `?version=${version}` : "";
  return send(() => getRequest(sessionPath(sessionId, `/plan${query}`), BEARER), convertBiaPlan, { retry: true });
}

/** GET /sessions/{id}/plan/versions. */
function listPlanVersions(sessionId: string): Promise<BiaPlanVersionInfo[]> {
  return send(() => getRequest(sessionPath(sessionId, "/plan/versions"), BEARER), convertBiaPlanVersions, {
    retry: true,
  });
}

/** POST /sessions/{id}/plan/revise — REVISE job; PLAN_VERSION_CONFLICT when `baseVersion` is not the current one. */
function revisePlan(sessionId: string, instruction: string, baseVersion: number): Promise<BiaSession> {
  return postForSession(sessionId, "/plan/revise", buildRevisePayload(instruction, baseVersion));
}

/** PUT /sessions/{id}/plan — a manual edit becomes a new version (source MANUAL). */
async function savePlan(sessionId: string, baseVersion: number, body: BiaPlanBody): Promise<BiaSession> {
  const contents = await send(
    () => putRequest(sessionPath(sessionId, "/plan"), buildUpdatePlanPayload(baseVersion, body), BEARER),
    (c) => c
  );
  return settleSession(sessionId, contents);
}

/** POST /sessions/{id}/plan/restore — an older version is copied into a new current one. */
function restorePlan(sessionId: string, version: number, baseVersion: number): Promise<BiaSession> {
  return postForSession(sessionId, "/plan/restore", buildRestorePayload(version, baseVersion));
}

/** GET /sessions/{id}/plan/export?format= — text for WhatsApp, JSON for the image render. */
function exportPlan(sessionId: string, format: BiaPlanExportFormat): Promise<BiaPlanExport> {
  return send(
    () => getRequest(sessionPath(sessionId, `/plan/export?format=${format.toLowerCase()}`), BEARER),
    convertBiaPlanExport,
    { retry: true }
  );
}

/** POST /sessions/{id}/plan/confirm — writes the tree to the business's sections and freezes the plan. */
function confirmPlan(sessionId: string, version: number): Promise<BiaSession> {
  return postForSession(sessionId, "/plan/confirm", buildConfirmPlanPayload(version));
}

/** POST /sessions/{id}/plan/unconfirm — back to PLAN_READY while nothing generated is created. */
function unconfirmPlan(sessionId: string, deleteEmptyCreatedSections: boolean): Promise<BiaSession> {
  return postForSession(sessionId, "/plan/unconfirm", buildUnconfirmPayload(deleteEmptyCreatedSections));
}

// ── Generate, payload, create ────────────────────────────────────────────────

/** POST /sessions/{id}/generate — GENERATE job for every group. */
function startGenerate(sessionId: string): Promise<BiaSession> {
  return postForSession(sessionId, "/generate", {});
}

/** POST /sessions/{id}/generate/retry — failed groups, or `productIds` (clientItemIds) to regenerate chosen ones. */
function retryGenerate(sessionId: string, productIds?: string[]): Promise<BiaSession> {
  return postForSession(sessionId, "/generate/retry", buildRetryGeneratePayload(productIds));
}

/** GET /sessions/{id}/payload?cursor=&limit= — generated products in tree order (paged). */
function getPayloadPage(sessionId: string, cursor?: string | null, limit?: number): Promise<BiaPayloadPage> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (limit) params.set("limit", String(limit));
  const query = params.toString();
  return send(
    () => getRequest(sessionPath(sessionId, `/payload${query ? `?${query}` : ""}`), BEARER),
    convertBiaPayloadPage,
    { retry: true }
  );
}

/**
 * POST /sessions/{id}/create-products — at most `createMaxPerCall` items,
 * idempotent per `clientItemId`. HTTP 200 even when some items fail (their
 * result carries the error); request-level problems are 4xx.
 */
function createProducts(sessionId: string, items: BiaCreateItemRq[]): Promise<BiaCreateProductsRs> {
  return send(
    () => postRequest(sessionPath(sessionId, "/create-products"), buildCreateProductsPayload(items), BEARER),
    convertBiaCreateProducts
  );
}

/** POST /sessions/{id}/finish — marks the remaining products SKIPPED and the session COMPLETED. */
function finishSession(sessionId: string): Promise<BiaSession> {
  return postForSession(sessionId, "/finish", {});
}

const BulkImageAnalysisV2ApiService = {
  getConfig,
  listSessions,
  getSession,
  createSession,
  updateSession,
  cancelSession,
  listImages,
  requestUploadUrls,
  confirmUploads,
  patchImage,
  deleteImage,
  uploadToSignedUrl,
  startPlan,
  getPlan,
  listPlanVersions,
  revisePlan,
  savePlan,
  restorePlan,
  exportPlan,
  confirmPlan,
  unconfirmPlan,
  startGenerate,
  retryGenerate,
  getPayloadPage,
  createProducts,
  finishSession,
};

export default BulkImageAnalysisV2ApiService;
